import { useCallback, useEffect, useState } from 'react';
import * as _ from 'lodash';
import { get, put } from '../../../redux/api';
import { useAddToast } from '../../../contexts/ToastContext';
import { useAsyncRequest, useAsyncValue, useIsDirty } from '../../../hooks';
import { errorMessagesListFromResponse } from '../../../helpers/errors';
import type { FailedFetchResponse } from '../../../helpers/errors';
import type { ThirdPartyFetcherLabel } from '../../../../shared/fetchers';

//  Template literal type for all settings endpoints.
export type OrganizationSettingsEndpoint = `/api/organizations/${number}/settings/${string}`;

type LanguageSettingSections = Extract<ThirdPartyFetcherLabel, 'Maven' | 'NPM' | 'Bower' | 'CocoaPods' | 'RPM' | 'Pip' | 'Nuget' | 'Git'>;

//  From a UI perspective, what are we calling each of the settings sections?
export type SettingsSection =
  | 'general'
  | 'portal'
  | 'portal token'
  | 'project general'
  | 'github status check'
  | 'privacy'
  | 'project licensing issues'
  | 'project security issues'
  | 'project quality issues'
  | 'project container'
  | 'project SBOM'
  | 'project notifications'
  | 'project update hooks'
  | 'authentication'
  | 'custom license scan'
  | LanguageSettingSections;

/**
 * Hook for fetching the settings.
 *
 * @param {OrganizationSettingsEndpoint} endpoint - The endpoint for the settings.
 */
function useFetchSettings<T>(endpoint: OrganizationSettingsEndpoint) {
  //  Note that we wrap this in a useCallback because useAsyncRequest will re-fetch if the request function changes.
  const fetchSettings = useCallback(() => get<T>(endpoint), [endpoint]);
  return useAsyncRequest(fetchSettings);
}

/**
 * Hook which manages the state after we fetch settings.
 * Allows the user to update the settings, and tracks if the settings are dirty.
 * Handles cases where the settings are an array or an object.
 *
 * @param {T} initialSettings - Settings after they are fetched.
 */
function useUpdateSettings<T>(initialSettings: T | null) {
  const [settings, setSettings] = useState<T | null>(initialSettings);
  const { isDirty, onUpdateInitialState } = useIsDirty<T | null>(initialSettings, settings);

  //  When new settings are fetched, we update the local copy which will be modified.
  useEffect(() => {
    if (initialSettings) {
      setSettings(initialSettings);
    }
  }, [initialSettings]);

  //  Ensures that we set isDirty to true, and then updates the local copy of the settings.
  //  Only a partial is required, so that the consumer doesn't need to know the rest of the settings state.
  //  We wrap this in a useCallback even though settings will be changing frequently.
  const onUpdateSettings = useCallback(
    (updatedSettings: T extends object ? Partial<T> : T) => {
      //  Settings could either be an object or an array,
      //  so we do the following check to handle it.
      //  Note that if the settings are an array, we assume that the array has
      //  already been updated and therefore we simply pass it to setSettings.
      if (Array.isArray(settings)) {
        setSettings(updatedSettings as T);
      } else {
        setSettings({
          ...settings,
          ...updatedSettings,
        } as T);
      }
    },
    [settings],
  );

  return {
    settings,
    isDirty,
    onUpdateInitialState,
    onUpdateSettings,
  };
}

/**
 * Hook for saving the settings.
 *
 * @param {OrganizationSettingsEndpoint} endpoint - The endpoint for the settings.
 */
function useSaveSettings<T>({
  endpoint,
  data,
  settingsSection,
  onSave,
}: {
  endpoint: OrganizationSettingsEndpoint;
  data: T | null;
  //  Used for describing the settings section in the toast.
  settingsSection: SettingsSection;
  //  Optional hook for when settings are saved, typically used for resetting isDirty.
  onSave?: () => void;
}) {
  const addToast = useAddToast();
  const { isLoading, errors, setPending, setResolve, setReject } = useAsyncValue<null>(null);

  //  Note that we wrap this in a useCallback because useAsyncRequest will re-fetch if the request function changes.
  const onSaveSettings = useCallback(async (dataOverride?: T) => {
    //  dataOverride will be used in place of the local copy of the settings.
    //  This is useful if the consumer needs to modify/normalize the settings before saving.
    const dataToPut = dataOverride || data;
    //  Unlikely that this would ever actually be null, but this helps for type safety below.
    if (dataToPut) {
      try {
        setPending();
        await put<T, void>(endpoint, dataToPut);
        setResolve(null);
        onSave?.();
        addToast({
          type: 'success',
          content: `${_.upperFirst(settingsSection)} settings saved`,
        });
      } catch (error: unknown) {
        setReject(errorMessagesListFromResponse(error as FailedFetchResponse));
        addToast({
          type: 'error',
          content: `Failed to save ${settingsSection} settings`,
        });
      }
    }
  }, [data, settingsSection, setPending, endpoint, addToast, setResolve, onSave, setReject]);

  return {
    isSaving: isLoading,
    errors,
    onSaveSettings,
  };
}

/**
 * Generic hook for all settings sections. Provided with simply the endpoint for a
 * given setting section, this hook will then manage a few different concerns:
 *
 *   - Making a GET request for the initial settings.
 *   - Storing the state of changes to those settings
 *   - Tracking if the changes are "dirty" (ie: have any changes been made)
 *   - Providing a callback which will make a PUT request to save the settings.
 *
 * @example
 * type DennisSettings = {
 *   diabloVersion: '2' | '3' | '4';
 *   isPlayingDiablo: boolean;
 * };
 *
 * const DennisSettings = () => {
 *   const {
 *     settings,
 *     isLoading,
 *     errors,
 *     onUpdateSettings,
 *     onSaveSettings,
 *     isSaveButtonDisabled,
 *   } = useOrganizationSettings<DennisSettings>('/api/organizations/1/settings/dennis');
 *
 *   if (isLoading.fetch) {
 *     return <Loader />;
 *   }
 *
 *   return (
 *     <>
 *       <h1>Dennis settings</h1>
 *       <LabeledCheckbox
 *         label="Is Dennis playing Diablo right now?"
 *         checked={settings.isPlayingDiablo}
 *         onChange={(isPlayingDiablo) => onUpdateSettings({ isPlayingDiablo })}
 *       />
 *       <LabeledSelect
 *         label="Diablo version"
 *         options={[
 *           { value: '2', label: 'Diablo 2' },
 *           { value: '3', label: 'Diablo 3' },
 *           { value: '4', label: 'Diablo 4' },
 *         ]}
 *         value={settings.diabloVersion}
 *         onChange={(diabloVersion) => onUpdateSettings({ diabloVersion })}
 *       />
 *       <Button
 *         isDisabled={isSaveButtonDisabled}
 *         onSave={onSaveSettings}
 *       >
 *         Save
 *       </Button>
 *     </>
 *   );
 * };
 */
export default function useOrganizationSettings<T>({
  endpoint,
  settingsSection,
}: {
  endpoint: OrganizationSettingsEndpoint;
  settingsSection: SettingsSection;
}) {
  //  Fetch the initial settings.
  const { value: initialSettings, isLoading, errors: fetchErrors } = useFetchSettings<T>(endpoint);
  //  Pass them into the hook which manages updating the settings.
  const { settings, isDirty, onUpdateInitialState, onUpdateSettings } = useUpdateSettings<T>(initialSettings);
  //  Callback + AsyncValue for saving the settings.
  const {
    onSaveSettings,
    isSaving,
    errors: savingErrors,
  } = useSaveSettings<T>({
    endpoint,
    settingsSection,
    data: settings,
    //  After we save, we need to "reset" the initial state so that dirty checks will be against the newly saved settings
    //  instead of the settings which were originally fetched in the GET request.
    onSave: useCallback(() => onUpdateInitialState(settings), [onUpdateInitialState, settings]),
  });

  return {
    /**
     * Settings object which is fetched from the server.
     */
    initialSettings,
    /**
     * Settings object which is actually read by the component and is updated.
     */
    settings,
    /**
     * Exposed so that we know if the "propagate settings" button should be disabled.
     */
    isDirty,
    /**
     * Network request statuses, grouped for convenience.
     */
    isLoading: {
      fetch: isLoading,
      save: isSaving,
      any: isLoading || isSaving,
    },
    /**
     * Collection of errors from either of the requests. Null if there are no errors.
     */
    errors: fetchErrors || savingErrors ? [...(fetchErrors || []), ...(savingErrors || [])] : null,
    /**
     * Method for updating the settings locally when they are changed.
     */
    onUpdateSettings,
    /**
     * Method passed directly to the save button to make the PUT request to save the settings.
     * Note that we are explicitly creating this closure so that nothing is passed as an argument,
     * because the argument will be used as the PUT request body, which should only be used when you
     * want to "override" the data being sent in the request (ie: maybe you want to normalize it).
     */
    onSaveSettings: useCallback(() => onSaveSettings(), [onSaveSettings]),
    /**
     * Same as above, except that you are able to provide a "dataOverride" which will be used
     * instead of the local copy of the `settings`. Do not pass this directly to the save button
     * as the onClick handler, as onClick will pass along an event object as the first argument,
     * instead use the above method if this is what you need.
     */
    onSaveSettingsWithOverride: onSaveSettings,
    /**
     * If the save button should be disabled for any reason.
     */
    isSaveButtonDisabled: !isDirty || isLoading || isSaving,
  };
}
